import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import styles from "./patch-ticket-status-dropdown.module.module.scss";
import SpinnerWithVariant from "../SpinnerWithVariant/spinner-with-variant";
import { SupportAgentTicket } from "@src/types/support";
import {
  errorSnackbar,
  successSnackbar,
  warningSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { patchTicketStatusUseCase } from "@src/cloud-connect/support/use-cases/patch-ticket-status-use-case";
import { useI18N } from "@src/language/language-context";
import { useClickOutsideListener } from "@src/hooks/use-click-outside-listener";
import { CustomTooltip } from "@src/components/atoms/custom-tooltip/custom-tooltip";

const cssClassName = "patch-ticket-status-dropdown";

type StatusType = {
  code: string;
  label: string;
};

type StatusArrayType = StatusType[];

interface PatchTicketStatusDropdownPropTypes {
  ticket: SupportAgentTicket;
  disabled?: boolean;
  disabledTooltip?: string;
  // PATCH response returns the updated ticket; propagate so the parent's copy
  // doesn't stay stale until the next SSE refetch
  onTicketUpdated?: (ticket: SupportAgentTicket) => void;
}

export const PatchTicketStatusDropdown = ({
  ticket,
  disabled = false,
  disabledTooltip = "",
  onTicketUpdated,
}: PatchTicketStatusDropdownPropTypes) => {
  const i18n = useI18N();

  const texts = {
    labelNew: i18n("modules.patchTicketStatusDropdown.labelNew"),
    labelOpen: i18n("modules.patchTicketStatusDropdown.labelOpen"),
    labelPending: i18n("modules.patchTicketStatusDropdown.labelPending"),
    labelHold: i18n("modules.patchTicketStatusDropdown.labelHold"),
    labelSolved: i18n("modules.patchTicketStatusDropdown.labelSolved"),
    errorUpdatingStatus: i18n(
      "modules.patchTicketStatusDropdown.errorUpdatingStatus",
    ),
    selectStatusLabel: i18n(
      "modules.patchTicketStatusDropdown.selectStatusLabel",
    ),
    successUpdatingStatus: i18n(
      "modules.patchTicketStatusDropdown.successUpdatingStatus",
    ),
    warningNoSolvedWithoutAssignee: i18n(
      "modules.patchTicketStatusDropdown.warningNoSolvedWithoutAssignee",
    ),
  };

  const STATUS_ARRAY: StatusArrayType = [
    { code: "new", label: texts.labelNew },
    {
      code: "open",
      label: texts.labelOpen,
    },
    {
      code: "pending",
      label: texts.labelPending,
    },
    {
      code: "hold",
      label: texts.labelHold,
    },
    {
      code: "solved",
      label: texts.labelSolved,
    },
  ];

  const DROPDOWN_STATUS_ARRAY: StatusArrayType = [
    {
      code: "open",
      label: texts.labelOpen,
    },
    {
      code: "pending",
      label: texts.labelPending,
    },
    {
      code: "hold",
      label: texts.labelHold,
    },
    {
      code: "solved",
      label: texts.labelSolved,
    },
  ];

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<StatusType | null>({
    code: "new",
    label: texts.labelNew,
  });
  const [isChangingStatus, setIsChangingStatus] = useState(false);

  const popoverRef = useRef(null);

  useClickOutsideListener(popoverRef, () => setOpen(false), open);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const handleSelect = async (statusCode: string) => {
    if (disabled || isChangingStatus || selected?.code === statusCode) return;
    if (ticket.assigned_to === null && statusCode === "solved") {
      warningSnackbar(texts.warningNoSolvedWithoutAssignee, 5000);
      setOpen(false);
      return;
    }
    setIsChangingStatus(true);
    const response = await patchTicketStatusUseCase(ticket.id, statusCode);

    if (!response.success) {
      errorSnackbar(texts.errorUpdatingStatus);
      setIsChangingStatus(false);
      setOpen(false);
    } else {
      const newSelected =
        STATUS_ARRAY.find((status) => status.code === statusCode) || null;
      setSelected(newSelected);
      setIsChangingStatus(false);
      successSnackbar(texts.successUpdatingStatus);
      setOpen(false);
    }
  };

  useEffect(() => {
    const currentStatus = STATUS_ARRAY.find(
      (status) => status.code === ticket.status,
    ) || {
      code: "new",
      label: texts.labelNew,
    };
    setSelected(currentStatus);
  }, [ticket]);

  return (
    <div ref={popoverRef} className={styles[cssClassName]}>
      <CustomTooltip
        content={disabled ? disabledTooltip : ""}
        className={styles[`${cssClassName}__tooltip-wrapper`]}
      >
        <button
          type="button"
          className={`${styles[`${cssClassName}__selector`]} ${
            disabled && disabledTooltip
              ? styles[`${cssClassName}__selector--sse-disabled`]
              : ""
          }`}
          onClick={() => setOpen(!open)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open && !disabled}
        >
          {isChangingStatus ? (
            <SpinnerWithVariant variant={["small", "secondary"]} />
          ) : (
            <p
              className={cn(
                styles[`${cssClassName}__text`],
                styles[
                  `${cssClassName}__text--${selected ? selected.code : ""}`
                ],
              )}
            >
              {selected ? selected.label : texts.selectStatusLabel}
            </p>
          )}
        </button>
      </CustomTooltip>

      {open && !disabled && (
        <ul
          className={styles[`${cssClassName}__list`]}
          role="listbox"
          aria-activedescendant={selected ? selected.code : undefined}
          tabIndex={0}
        >
          {DROPDOWN_STATUS_ARRAY.map((status) => (
            <li
              key={status.code}
              id={status.code}
              role="option"
              aria-selected={selected ? selected.code === status.code : false}
            >
              <button
                type="button"
                className={styles[`${cssClassName}__item`]}
                onClick={() => handleSelect(status.code)}
                disabled={disabled || isChangingStatus}
              >
                <p
                  className={cn(
                    styles[`${cssClassName}__text`],
                    styles[`${cssClassName}__text--${status.code}`],
                  )}
                >
                  {status.label}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
